import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../constants";
import { handelLogout, setAccessToken } from "../reducers/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getCurrentDateTime } from "../helpers/datetime";
import { useRef } from "react";
import { TRootState } from "../store";
import { resetLoanFilters } from "../reducers/loansSlice";
const useAxiosClient = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  /** Setting common axios header  */
  const currentPath = location.pathname.split("/").filter(Boolean)[0];

  const { adminId, accessToken } = useSelector((state: TRootState) => ({
    adminId: state.auth.adminId,
    accessToken: state.auth.accessToken,
  }));

  localStorage.setItem("selectedPath", currentPath);

  const selectedPath = localStorage.getItem("selectedPath");

  const traderId = "1";
  const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
  });

  const authInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
  });

  axiosInstance.defaults.headers.common["Content-Type"] = "application/json";

  if (accessToken) {
    axiosInstance.defaults.headers.common["Authorization"] =
      `Bearer ${accessToken}`;
  }
  const navigate = useNavigate();

  let refreshPromise: Promise<string> | null = null;
  const refreshAccessToken = async (): Promise<string> => {
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        const response = await authInstance.post(
          `account/mos/refresh-token`,
          {
            userId: adminId,
            type: "MPOWER_ADMIN",
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const newAccessToken = response?.data?.data;

        if (!newAccessToken) {
          throw new Error("New access token not found");
        }

        dispatch(setAccessToken(newAccessToken));

        axiosInstance.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;

        return newAccessToken;
      } catch (error: any) {
        if (error?.response?.status === 401) {
          await handleLogoutFun();
        }

        throw error;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  axiosInstance.interceptors.response.use(
    (response) => response,

    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        try {
          const newAccessToken = await refreshAccessToken();

          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${newAccessToken}`,
          };

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // Refresh token expired/invalid
          handleLogoutFun();

          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    },
  );

  /** Function to attach click id in URL */
  const handleCreateURL = (_url = "") => {
    let newURL = _url;
    if (_url.includes(":traderId")) {
      newURL = _url.replace(":traderId", traderId);
      return newURL;
    }

    if (_url.includes(":traderId")) {
      newURL = _url.replace(":traderId", traderId);
    }

    return newURL;
  };

  const handleLogoutFun = async () => {
    try {
      await authInstance.post(`account/mos/logout`, [
        {
          userId: adminId,
          type: "MPOWER_ADMIN",
        },
      ]);
    } catch (error) {
      console.error("Admin logout API failed:", error);
    }

    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
    localStorage.clear();
    dispatch(handelLogout(null));
    navigate("/");
    dispatch(resetLoanFilters());
    window.location.reload();
    //@ts-ignore
    // oNoLocalStorage.set("LinkPath", "/smartboard", false);
    // //@ts-ignore
    // oNoLocalStorage.set("statusPlan", "FAILURE", false);
    // //@ts-ignore
    // oNoLocalStorage.set("LinkId", "1", false);
    //@ts-ignore
    localStorage.setItem("isauth", false);
  };

  const displayedToasts = useRef<Set<string>>(new Set());
  const handleGetCall = <T>(url: string): Promise<{ data: T }> => {
    return new Promise((resolve, reject) => {
      const apiURL = handleCreateURL(url);
      axiosInstance
        .get(`${apiURL}`)
        .then(({ data }) => {
          resolve({ data: data.data });
        })
        .catch((error: any) => {
          const logPayload = {
            statusCode: error?.response?.status?.toString() || "NETWORK_ERROR",
            message: error?.message || "Unknown error occurred",
            timestamp: new Date().toISOString(),
            page: window.location.pathname,
            context: `API_CALL_FAILURE: ${apiURL}`,
          };
          if (error.message && error.message.includes("Network Error")) {
            showToast("Network Error: Unable to reach the server.");
            sendErrorLog(logPayload);
          } else if (
            error.response.data &&
            error.response.data.status.message === "INVALID CREDENTIALS"
          ) {
            // handleLogoutFun();
          } else if (
            error.status == 400 ||
            error.status == 500 ||
            error.status == 502 ||
            error.status == 503
          ) {
            if (error.response.status === 502) {
              sendErrorLog(logPayload);
            }
            if (error.response.data.status === 404) {
              showToast(error.response.data.error);
            } else {
              showToast(error.response.data.status.description);
            }
          }

          reject(error);
        });
    });
  };

  const sendErrorLog = async (payload: {
    statusCode: string;
    message: string;
    timestamp: string;
    page: string;
    context: string;
  }) => {
    try {
      await fetch(`${API_BASE_URL}common/frontend-logs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
    } catch (logError) {
      console.error("Failed to log error:", logError);
    }
  };

  const handlePostCall = <T>({
    URL = "",
    apiParams = {},
    config = {},
    isAuthInstance = false,
  }): Promise<{ data: T; status: any }> => {
    return new Promise((resolve, reject) => {
      const apiURL = handleCreateURL(URL);
      let apiInstance = isAuthInstance ? authInstance : axiosInstance;
      apiInstance
        .post(`${apiURL}`, apiParams, config)
        .then(({ data }) => {
          resolve({ data: data.data, status: data.status });
        })
        .catch((error: any) => {
          const logPayload = {
            statusCode: error?.response?.status?.toString() || "NETWORK_ERROR",
            message: error?.message || "Unknown error occurred",
            timestamp: new Date().toISOString(),
            page: window.location.pathname, // or hardcode like "HomePage"
            context: `API_CALL_FAILURE: ${apiURL}`, // Describe where it failed
          };
          if (error.message && error.message.includes("Network Error")) {
            toast.error("Network Error: Unable to reach the server.");
            sendErrorLog(logPayload);
          } else if (
            error.response.data.status.message === "INVALID CREDENTIALS"
          ) {
            // handleLogoutFun();
          } else if (
            error.status == 400 ||
            error.status == 500 ||
            error.status == 502 ||
            error.status == 503 ||
            error.status == 404
          ) {
            if (error.response.status === 502) {
              sendErrorLog(logPayload);
            }
            if (error.status == 404) {
              toast.error(error.response.data.error);
            } else {
              toast.error(error.response.data.status.description);
            }
          }

          reject(error);
        });
    });
  };

  const handlePutCall = <T>({
    URL = "",
    apiParams = {},
  }): Promise<{ data: T; status: any }> => {
    return new Promise((resolve, reject) => {
      const apiURL = handleCreateURL(URL);
      axiosInstance
        .put(`${apiURL}`, apiParams)
        .then(({ data }) => {
          resolve({ data: data.data, status: data.status });
        })
        .catch((error: any) => {
          if (error.response.status === 502) {
            console.log(
              `[${apiURL}  ${getCurrentDateTime()}] 502 Bad Gateway Error:`,
              error.response.data,
            );
          }
          if (error.message && error.message.includes("Network Error")) {
            toast.error("Network Error: Unable to reach the server.");
          } else if (
            error.response.data.status.message === "INVALID CREDENTIALS"
          ) {
            // handleLogoutFun();
          } else if (
            error.status == 400 ||
            error.status == 500 ||
            error.status == 502 ||
            error.status == 503
          ) {
            toast.error(error.response.data.status.description);
          }
          reject(error);
        });
    });
  };

  const showToast = (message: string) => {
    if (!displayedToasts.current.has(message)) {
      toast.error(message);
      displayedToasts.current.add(message);
    }
  };

  const handleDeleteCall = <T>({
    URL = "",
    apiParams = {},
  }): Promise<{ data: T; status: any }> => {
    return new Promise((resolve, reject) => {
      const apiURL = handleCreateURL(URL);

      axiosInstance
        .delete(`${apiURL}`, { data: apiParams })
        .then(({ data }) => {
          resolve({ data: data.data, status: data.status });
        })
        .catch((error: any) => {
          if (error.response?.status === 502) {
            console.log(
              `[${apiURL}  ${getCurrentDateTime()}] 502 Bad Gateway Error:`,
              error.response.data,
            );
          }

          if (error.message && error.message.includes("Network Error")) {
            toast.error("Network Error: Unable to reach the server.");
          } else if (
            error.response?.data?.status?.message === "INVALID CREDENTIALS"
          ) {
            // handleLogoutFun();
          } else if (
            error.response?.status == 400 ||
            error.response?.status == 500 ||
            error.response?.status == 502 ||
            error.response?.status == 503
          ) {
            toast.error(error.response?.data?.status?.description);
          }

          reject(error);
        });
    });
  };

  return {
    handlePutCall,
    handleGetCall,
    handlePostCall,
    handleDeleteCall,
    handleLogoutFun,
  };
};

export default useAxiosClient;
